import { EXTERNAL_AUTHORITY_LABEL, type AskSource, type ExternalResearchLog } from '@buakei/types';
import { ExternalLink } from 'lucide-react';

/**
 * External authorities found by common-law research (ADR-038), shown apart from Sierra Leone
 * sources with their status, origin and how much of the page was read. None is binding
 * Sierra Leone law; the status says what it is.
 */

export function externalMeta(s: AskSource): string {
  const e = s.external;
  if (!e) return '';
  return [
    [e.court, e.jurisdiction].filter(Boolean).join(', '),
    e.primary ? 'primary source' : e.tier === 3 ? 'educational source' : 'secondary source',
    `${e.domain}, accessed ${new Date(e.accessedAt).toLocaleDateString('en-GB')}`,
    e.inspected === 'partial' ? 'page read in part' : 'page read in full',
  ].join(' · ');
}

export function ExternalSourceItem({ s, id }: { s: AskSource; id?: string }) {
  const e = s.external!;
  return (
    <li id={id} className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-navy-900">
        [{s.n}] {s.title}
        {e.citation && !s.title.includes(e.citation) ? ` ${e.citation}` : ''} — {s.label}
      </span>
      <span className="text-ink-900">{EXTERNAL_AUTHORITY_LABEL[e.authority]}</span>
      {e.primaryNotInspected ? (
        <span className="text-ink-900">
          Educational commentary, not the judgment. The judgment
          {e.primaryNotInspected.citation ? ` ${e.primaryNotInspected.citation}` : ''} was not
          inspected because {e.primaryNotInspected.reason}.{' '}
          {e.primaryNotInspected.url ? (
            <a
              href={e.primaryNotInspected.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-navy-900 underline"
            >
              Open the judgment<span className="sr-only"> (opens in a new tab)</span>
            </a>
          ) : null}
        </span>
      ) : null}
      <span className="text-xs text-muted-600">{externalMeta(s)}</span>
      {s.quotes.map((q) => (
        <blockquote key={q} className="border-l-2 border-line-200 pl-3 font-serif text-ink-900">
          “{q}”
        </blockquote>
      ))}
      <a
        href={e.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 self-start font-medium text-navy-900 underline"
      >
        <ExternalLink className="size-4" aria-hidden /> Open the source on {e.domain}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    </li>
  );
}

const EVENT_LABEL: Record<ExternalResearchLog['events'][number]['kind'], string> = {
  searched: 'Searched',
  opened: 'Read',
  discovery: 'Used for discovery only',
  not_found: 'Could not retrieve',
  refused: 'Search refused',
  failed: 'Search failed',
};

/** What external research did for an answer: searches, pages read, gaps. */
export function ExternalResearchLogView({ logs }: { logs: ExternalResearchLog[] }) {
  return (
    <details className="rounded-md border border-line-200 px-3 py-2 text-sm">
      <summary className="cursor-pointer font-medium text-navy-900">
        How external sources were researched
      </summary>
      <div className="mt-2 flex flex-col gap-3">
        {logs.map((log, i) => (
          <div key={i} className="flex flex-col gap-1">
            <p className="text-ink-900">
              <strong>Issue:</strong> {log.issue}
            </p>
            <ul className="flex flex-col gap-1 pl-4 text-ink-900">
              {log.events.map((e, j) => (
                <li key={j} className="list-disc">
                  {EVENT_LABEL[e.kind]}: {e.title ?? e.note}
                  {e.title ? <span className="text-muted-600"> ({e.note})</span> : null}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-600">
              Searched {new Date(log.at).toLocaleString('en-GB')} in approved legal sources only.
              Not an exhaustive search: an authority not found here may still exist.
            </p>
          </div>
        ))}
      </div>
    </details>
  );
}
